// BUILD-046 Owner-requested polish (visual edits ledger, session edit 4.5): OBVIOUS SORTING - what the clickable column headings of the task table mean.
//
// Finish-b (Owner direction of 2026-10-20, item 3: truthful sorting): the headings Task, Assigned to, Priority, Task due date and Status are buttons that change the
// page's ONE sort choice (pageSortModel.ts), the same state the "Sort: <selection>" dropdown reads and writes - choosing "Task due date: latest first" in the dropdown
// puts the arrow on Task due date, and clicking a heading changes the dropdown's label and the order. EVERY click on a heading toggles its direction and NEVER falls
// back to Recommended. The first click on an idle heading, then the reverse, then back again:
//
//   Task           : A-Z (ascending, arrow up)               <->  Z-A (descending, arrow down)
//   Assigned to    : A-Z (ascending, arrow up)               <->  Z-A (descending, arrow down)                (Team / Company tables only)
//   Priority       : highest first (descending, arrow down)  <->  lowest first (ascending, arrow up)          (unset priority last either way)
//   Due            : earliest first (ascending, arrow up)    <->  latest first (descending, arrow down)       (undated last either way; the heading was "Task due date" before 1a)
//   Status         : Active to Completed (ascending, up)     <->  Completed to Active (descending, down)      (Team / Company tables only)
//                    The ONE status order is Active, Waiting, Blocked, Completed; the Status heading says so in its name and tooltip.
//
// `aria-sort` is the true data direction (see the table). The arrow is drawn on the active heading only. Recommended, Oldest, Newest and Project have no column of
// their own: while one of them is the sort, no heading shows an arrow, and every heading is aria-sort "none". The orders themselves are taskOrdering.ts's.
//
// Sorting only ever covers the tasks that are LOADED: `sortScopeText` is the quiet sentence a partial list (Completed Team / Company work with Load more) shows.
//
// Pure module. Type-only import. Erasable TypeScript only.

import type { PageSortMode } from './pageSortModel';
import { STATUS_ORDER_TEXT } from './taskOrdering';
import { PROGRESS_ORDER_TEXT } from './issuedRowModel';

// Increment 1a: the Due heading is "Due" (the column of every task table); its accessible name and tooltip keep the full name, "Due (Task due date)". Assigned by Me adds three
// columns of its own: Assigned to (by the first name listed), Status / progress (its own order) and Assigned / age (the original assignment time).
export type SortColumn = 'title' | 'assignee' | 'priority' | 'due' | 'status' | 'responsible' | 'progress' | 'assigned';
export type AriaSortValue = 'ascending' | 'descending' | 'none';

export interface SortHeading {
  column: SortColumn;
  /** The heading's text ("Task", "Priority", "Due" ...). */
  label: string;
  /** How the text is drawn: one line, or two for "Assigned / Age" (Owner direction 2026-10-23). */
  lines: readonly string[];
  /** True when a click can do something: the mode it selects is offered in this view. */
  clickable: boolean;
  /** True when this column is the current sort. */
  active: boolean;
  /** For `aria-sort` on the columnheader. */
  ariaSort: AriaSortValue;
  /** The arrow to draw: only on the active heading. */
  arrow: 'up' | 'down' | null;
  /** The mode a click selects (the OTHER direction while this column is the sort, else the column's first direction). Null when the heading is not clickable. */
  next: PageSortMode | null;
  /** The heading button's complete accessible name (also its tooltip). */
  ariaLabel: string;
}

export const DEFAULT_SORT_MODE: PageSortMode = 'recommended';

interface Direction {
  mode: PageSortMode;
  /** The order in words, as the accessible name says it. */
  words: string;
  ariaSort: 'ascending' | 'descending';
}

interface ColumnSpec {
  label: string;
  /** The heading drawn on more than one line; the label stays its one name. */
  lines?: readonly string[];
  /** The column's full name when the heading is shortened ("Task due date" for "Due"): the accessible name and the tooltip carry it. */
  aka?: string;
  /** The direction a click on an idle heading selects. */
  first: Direction;
  /** The reverse. */
  second: Direction;
  /** Extra words appended to the accessible name (Status states its one order). */
  note?: string;
}

const SPECS: Readonly<Record<SortColumn, ColumnSpec>> = Object.freeze({
  title: { label: 'Task', first: { mode: 'title-asc', words: 'A to Z', ariaSort: 'ascending' }, second: { mode: 'title-desc', words: 'Z to A', ariaSort: 'descending' } },
  assignee: { label: 'Assigned to', first: { mode: 'assignee-asc', words: 'A to Z', ariaSort: 'ascending' }, second: { mode: 'assignee-desc', words: 'Z to A', ariaSort: 'descending' } },
  priority: { label: 'Priority', first: { mode: 'priority', words: 'highest first', ariaSort: 'descending' }, second: { mode: 'priority-low', words: 'lowest first', ariaSort: 'ascending' } },
  due: { label: 'Due', aka: 'Task due date', first: { mode: 'due-soonest', words: 'earliest first', ariaSort: 'ascending' }, second: { mode: 'due-latest', words: 'latest first', ariaSort: 'descending' } },
  status: {
    label: 'Status',
    first: { mode: 'status-asc', words: 'Active to Completed', ariaSort: 'ascending' },
    second: { mode: 'status-desc', words: 'Completed to Active', ariaSort: 'descending' },
    note: `Status order: ${STATUS_ORDER_TEXT}.`,
  },
  // Assigned by Me only. A request can name several people, none (an Anyone request nobody has taken names a group) or only a group, so the name says which name is sorted.
  responsible: {
    label: 'Assigned to',
    first: { mode: 'responsible-asc', words: 'A to Z by the first name listed', ariaSort: 'ascending' },
    second: { mode: 'responsible-desc', words: 'Z to A by the first name listed', ariaSort: 'descending' },
    note: 'A request is ordered by the first name listed in its cell (for an Anyone request nobody has taken, its first group); a request with no name to show is last.',
  },
  progress: {
    label: 'Status',
    first: { mode: 'progress-asc', words: 'Unclaimed, then Active to Completed', ariaSort: 'ascending' },
    second: { mode: 'progress-desc', words: 'Completed to Active, Unclaimed last', ariaSort: 'descending' },
    note: `Order: ${PROGRESS_ORDER_TEXT}. An Everyone request counts as Active until every recipient is done, then Completed; the smaller share done comes first.`,
  },
  assigned: {
    label: 'Assigned / Age',
    lines: ['Assigned /', 'Age'],
    first: { mode: 'assigned-newest', words: 'newest first', ariaSort: 'descending' },
    second: { mode: 'assigned-oldest', words: 'oldest first', ariaSort: 'ascending' },
    note: 'By the date the request was originally assigned; a request with no date is last.',
  },
});

/** The two modes that sort a column, first direction first. */
export const COLUMN_MODES: Readonly<Record<SortColumn, readonly PageSortMode[]>> = Object.freeze({
  title: Object.freeze([SPECS.title.first.mode, SPECS.title.second.mode]),
  assignee: Object.freeze([SPECS.assignee.first.mode, SPECS.assignee.second.mode]),
  priority: Object.freeze([SPECS.priority.first.mode, SPECS.priority.second.mode]),
  due: Object.freeze([SPECS.due.first.mode, SPECS.due.second.mode]),
  status: Object.freeze([SPECS.status.first.mode, SPECS.status.second.mode]),
  responsible: Object.freeze([SPECS.responsible.first.mode, SPECS.responsible.second.mode]),
  progress: Object.freeze([SPECS.progress.first.mode, SPECS.progress.second.mode]),
  assigned: Object.freeze([SPECS.assigned.first.mode, SPECS.assigned.second.mode]),
});

/** Which column (if any) the sort mode is the sort of. Recommended, Oldest, Newest and Project have no column. */
export function columnOfMode(mode: PageSortMode): SortColumn | null {
  for (const column of Object.keys(SPECS) as SortColumn[]) {
    if (SPECS[column].first.mode === mode || SPECS[column].second.mode === mode) return column;
  }
  return null;
}

/**
 * What one heading is, given the sort in force and the modes the view offers. A click on an idle heading selects the column's first direction; a click on the active
 * heading selects the other direction. It never selects Recommended. The heading is clickable only when the mode a click selects is offered in this view.
 */
export function describeSortHeading(column: SortColumn, mode: PageSortMode, available: readonly PageSortMode[], extraNote?: string): SortHeading {
  const spec = SPECS[column];
  const offers = (candidate: PageSortMode): boolean => available.includes(candidate);
  const current = mode === spec.first.mode ? spec.first : mode === spec.second.mode ? spec.second : null;
  // The accessible name carries the full column name when the heading is shortened: "Sort by Due (Task due date), earliest first".
  const subject = spec.aka === undefined ? spec.label : `${spec.label} (${spec.aka})`;
  // Status adds its one order to the name (and so to the tooltip): "Sort by Status, Active to Completed. Status order: Active, Waiting, Blocked, Completed." A table may add a note of
  // its own (`extraNote`): Assigned by Me says that an Everyone request carries no due date or priority and so is last.
  const named = (text: string): string => [text, spec.note, extraNote].filter((part): part is string => typeof part === 'string' && part !== '').join('. ');
  if (current === null) {
    const target = spec.first;
    const clickable = offers(target.mode);
    return {
      column,
      label: spec.label,
      lines: spec.lines ?? [spec.label],
      clickable,
      active: false,
      ariaSort: 'none',
      arrow: null,
      next: clickable ? target.mode : null,
      ariaLabel: named(`Sort by ${subject}, ${target.words}`),
    };
  }
  const target = current === spec.first ? spec.second : spec.first;
  const clickable = offers(target.mode);
  return {
    column,
    label: spec.label,
    lines: spec.lines ?? [spec.label],
    clickable,
    active: true,
    ariaSort: current.ariaSort,
    arrow: current.ariaSort === 'ascending' ? 'up' : 'down',
    next: clickable ? target.mode : null,
    ariaLabel: named(`Sort by ${subject}, currently ${current.words}, click for ${target.words}`),
  };
}

/**
 * The quiet sentence that keeps sorting truthful on a list that is read page by page: null when the list is whole, else
 * "Sorted within the N loaded tasks; load more to include the rest." (the same style as the search sentence).
 */
export function sortScopeText(list: { complete: boolean; loadedCount: number }, nouns: { singular: string; plural: string } = { singular: 'task', plural: 'tasks' }): string | null {
  if (list.complete) return null;
  const count = Number.isFinite(list.loadedCount) && list.loadedCount >= 0 ? Math.floor(list.loadedCount) : 0;
  return `Sorted within the ${count} loaded ${count === 1 ? nouns.singular : nouns.plural}; load more to include the rest.`;
}
